import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useStore } from "@/lib/theme";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { inr, formatDate, statusColor } from "@/lib/format";

export default function CustomerDashboard() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { tenant } = useStore();
  const nav = useNavigate();

  useEffect(() => {
    if (!user || user.role !== "customer") nav(`/t/${slug}/login`);
  }, [user, slug, nav]);

  const b = tenant?.branding || {};

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: b.heading_font, color: b.primary_color }} data-testid="customer-dashboard-title">My Account</h1>
      <p className="opacity-70 text-sm mb-6">Hi {user?.name}, welcome back!</p>
      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders" data-testid="tab-my-orders">Rentals</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-my-tickets">Service Tickets</TabsTrigger>
          <TabsTrigger value="rewards" data-testid="tab-my-rewards">Rewards</TabsTrigger>
        </TabsList>
        <TabsContent value="orders"><OrdersTab slug={slug} brand={b} /></TabsContent>
        <TabsContent value="tickets"><TicketsTab brand={b} /></TabsContent>
        <TabsContent value="rewards"><RewardsTab tenant={tenant} brand={b} /></TabsContent>
      </Tabs>
    </div>
  );
}

function OrdersTab({ slug, brand }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/customer/orders").then(({ data }) => setOrders(data)); }, []);
  return (
    <Card className="t-card t-surface p-5 mt-4">
      {orders.length === 0 && <p className="opacity-60 text-sm">No rentals yet.</p>}
      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.id} className="border rounded-lg p-4" style={{ borderColor: brand.primary_color + "33" }} data-testid={`my-order-${o.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-medium">Order #{o.id.slice(0, 8)} • {formatDate(o.created_at)}</div>
                <div className="text-xs opacity-70 mt-1">{o.items.map((i) => `${i.product_name} × ${i.qty}`).join(", ")}</div>
              </div>
              <div className="text-right">
                <div className="font-bold">{inr(o.total)}</div>
                <div className="flex gap-1 justify-end mt-1">
                  <span className={`pill ${statusColor(o.status)}`}>{o.status}</span>
                  <span className={`pill ${statusColor(o.payment_status)}`}>{o.payment_status}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TicketsTab({ brand }) {
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", order_id: "", issue_type: "Not working", description: "", photos: [], preferred_date: "" });
  const refresh = () => {
    api.get("/customer/tickets").then(({ data }) => setTickets(data));
    api.get("/customer/orders").then(({ data }) => setOrders(data));
  };
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    if (!form.product_id) { toast.error("Pick a product from your rentals"); return; }
    try {
      await api.post("/customer/tickets", form);
      toast.success("Ticket raised");
      setOpen(false); setForm({ product_id: "", order_id: "", issue_type: "Not working", description: "", photos: [], preferred_date: "" });
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const allProducts = orders.flatMap((o) => o.items.map((i) => ({ id: i.product_id, name: i.product_name, order_id: o.id })));
  return (
    <Card className="t-card t-surface p-5 mt-4">
      <div className="flex justify-end mb-4"><Button onClick={() => setOpen(true)} style={{ background: brand.primary_color, color: "#fff" }} data-testid="ticket-raise-btn">Raise Ticket</Button></div>
      {tickets.length === 0 && <p className="opacity-60 text-sm">No tickets raised.</p>}
      <div className="space-y-3">
        {tickets.map((t) => (
          <div key={t.id} className="border rounded p-3" style={{ borderColor: brand.primary_color + "33" }} data-testid={`my-ticket-${t.id}`}>
            <div className="flex justify-between"><div><div className="font-medium">{t.issue_type}</div><div className="text-xs opacity-70 mt-1">{t.description}</div></div><span className={`pill ${statusColor(t.status)}`}>{t.status}</span></div>
            {t.technician_notes && <div className="text-xs mt-2 bg-slate-50 p-2 rounded">Technician: {t.technician_notes}</div>}
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Raise Service Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Product (from your orders)</Label>
              <Select value={form.product_id} onValueChange={(v) => { const item = allProducts.find((p) => p.id === v); setForm({ ...form, product_id: v, order_id: item?.order_id || "" }); }}>
                <SelectTrigger data-testid="ticket-product-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{allProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Issue type</Label>
              <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Not working", "Damaged", "Installation", "Service Due", "Other"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ticket-desc-input" /></div>
            <div><Label>Preferred date</Label><Input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} /></div>
            <Button className="w-full" onClick={save} data-testid="ticket-submit-btn">Submit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RewardsTab({ tenant, brand }) {
  const [data, setData] = useState({ points: 0, transactions: [] });
  useEffect(() => { api.get("/customer/rewards").then(({ data }) => setData(data)); }, []);
  return (
    <Card className="t-card t-surface p-5 mt-4" data-testid="rewards-tab">
      <div className="rounded-xl p-6 mb-4 text-center" style={{ background: brand.primary_color, color: "#fff" }}>
        <div className="text-sm opacity-80">Your reward points</div>
        <div className="text-5xl font-bold mt-1" style={{ fontFamily: brand.heading_font }}>{data.points}</div>
        <div className="text-xs opacity-80 mt-1">1 point = {inr(tenant?.point_value_rupee || 1)} • Earn {tenant?.points_per_rupee || 0.01} pts per ₹ spent</div>
      </div>
      <h4 className="font-bold mb-2">Transactions</h4>
      <div className="space-y-2 text-sm">
        {data.transactions.map((t) => (
          <div key={t.id} className="flex justify-between border-b border-slate-100 pb-2">
            <div>{t.description} <span className="text-xs opacity-60">{formatDate(t.created_at)}</span></div>
            <div className={t.points >= 0 ? "text-emerald-700 font-medium" : "text-rose-600 font-medium"}>{t.points >= 0 ? "+" : ""}{t.points}</div>
          </div>
        ))}
        {data.transactions.length === 0 && <p className="opacity-60">No rewards yet.</p>}
      </div>
    </Card>
  );
}
